import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { CraftHtmlTooltipComponent } from './craft-html-tooltip.component';

type TooltipPosition = 'above' | 'below' | 'left' | 'right';

@Directive({
  selector: '[craftHtmlTooltip]',
  standalone: true,
})
export class CraftHtmlTooltipDirective implements OnDestroy {
  @Input('craftHtmlTooltip') tooltipContent: TemplateRef<any> | null = null;
  @Input() craftHtmlTooltipPosition: TooltipPosition = 'above';
  @Input() craftHtmlTooltipShowDelay = 150;
  @Input() craftHtmlTooltipHideDelay = 100;
  @Input() craftHtmlTooltipContext: any;

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<CraftHtmlTooltipComponent> | null = null;
  private showTimerId: any;
  private hideTimerId: any;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone,
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.scheduleShow();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.scheduleHide(true);
  }

  show(immediate = false): void {
    if (!this.tooltipContent) {
      return;
    }

    this.clearTimers();

    const doShow = () => {
      if (!this.overlayRef) {
        this.overlayRef = this.createOverlay();
      }
      if (this.overlayRef && !this.overlayRef.hasAttached()) {
        this.componentRef = this.overlayRef.attach(
          new ComponentPortal(CraftHtmlTooltipComponent, this.viewContainerRef),
        );
      }
      if (this.componentRef) {
        this.componentRef.instance.template = this.tooltipContent;
        this.componentRef.instance.context = this.craftHtmlTooltipContext;
      }
    };

    if (immediate || this.craftHtmlTooltipShowDelay <= 0) {
      doShow();
    } else {
      this.showTimerId = this.ngZone.runOutsideAngular(() =>
        setTimeout(() => this.ngZone.run(doShow), this.craftHtmlTooltipShowDelay),
      );
    }
  }

  hide(immediate = false): void {
    this.clearTimers();

    const doHide = () => {
      if (this.overlayRef?.hasAttached()) {
        this.overlayRef.detach();
        this.componentRef = null;
      }
    };

    if (immediate || this.craftHtmlTooltipHideDelay <= 0) {
      doHide();
    } else {
      this.hideTimerId = this.ngZone.runOutsideAngular(() =>
        setTimeout(() => this.ngZone.run(doHide), this.craftHtmlTooltipHideDelay),
      );
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.componentRef = null;
    }
  }

  private scheduleShow(): void {
    this.hide(false);
    this.show(false);
  }

  private scheduleHide(immediate: boolean): void {
    if (this.showTimerId) {
      clearTimeout(this.showTimerId);
      this.showTimerId = null;
    }
    this.hide(immediate);
  }

  private clearTimers(): void {
    if (this.showTimerId) {
      clearTimeout(this.showTimerId);
      this.showTimerId = null;
    }
    if (this.hideTimerId) {
      clearTimeout(this.hideTimerId);
      this.hideTimerId = null;
    }
  }

  private createOverlay(): OverlayRef {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withPositions(this.mapPositions(this.craftHtmlTooltipPosition));

    return this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: ['mdc-tooltip', 'size-300'],
    });
  }

  private mapPositions(pos: TooltipPosition): ConnectedPosition[] {
    switch (pos) {
      case 'below':
        return [
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
        ];
      case 'left':
        return [
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
        ];
      case 'right':
        return [
          { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
          { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
        ];
      case 'above':
      default:
        return [
          { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
        ];
    }
  }
}
